import { Button, type ButtonHandle, type ButtonProps } from "@progress/kendo-react-buttons";
import type { RefObject } from "react";

type ActionButtonVariant = "primary" | "secondary" | "tertiary" | "plane" | "highlighted" | "icon";
type BaseActionButtonProps = ButtonProps & {
    ref?: RefObject<ButtonHandle | null>; innerClassName?: string;
};
type ActionButtonProps
    = (BaseActionButtonProps & { variant?: "primary" | "secondary" | "tertiary" | "plane" | "highlighted" })
        | (BaseActionButtonProps & { variant: "icon"; "aria-label": string });

const BASE_CLASS_NAME = "px-3 fw-semibold";
const ACTION_BUTTON_VARIANTS = {
    primary: {
        fillMode: "solid" as const,
        className: `${BASE_CLASS_NAME} !k-text-white !k-bg-primary bg-action-primary-hover`,
        style: undefined
    },
    secondary: {
        fillMode: "outline" as const,
        className: `${BASE_CLASS_NAME} !k-text-primary bg-action-secondary-hover !k-border-brand-primary`,
        style: undefined
    },
    tertiary: {
        fillMode: "clear" as const,
        className: `${BASE_CLASS_NAME} !k-text-tertiary text-highlight-primary-hover bg-action-tertiary-hover`,
        style: undefined
    },
    plane: {
        fillMode: "clear" as const,
        className: "px-2 fs-9 !k-text-primary bg-action-tertiary-hover rounded-2 !border-0 shadow-none text-uppercase fw-semibold",
        style: { letterSpacing: "1px" }
    },
    highlighted: {
        fillMode: "clear" as const,
        className: `${BASE_CLASS_NAME} fs-8 !k-text-primary bg-action-tertiary-hover rounded-2 !border-0 shadow-none`,
        style: undefined
    },
    icon: {
        fillMode: "clear" as const,
        className: "p-2 fw-medium rounded-2 !k-text-tertiary text-highlight-primary-hover bg-action-icon-hover",
        style: undefined
    }
} as const satisfies Record<ActionButtonVariant, { fillMode: ButtonProps["fillMode"]; className: string; style: object | undefined }>;

const ActionButton = ({
    variant = "tertiary",
    className,
    innerClassName,
    ref,
    type = "button",
    fillMode,
    children,
    ...rest
}: ActionButtonProps) => {
    const { fillMode: variantFillMode, className: variantClassName, style } = ACTION_BUTTON_VARIANTS[variant];

    return (
        <Button
            ref={ref}
            type={type}
            style={style}
            fillMode={fillMode ?? variantFillMode}
            className={["fs-7", innerClassName, className ?? variantClassName].filter(Boolean).join(" ")}
            {...rest}
        >
            {children}
        </Button>
    );
};

export default ActionButton;
